import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { CircleMarker, MapContainer, Popup, TileLayer } from "react-leaflet";
import { LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";

import { ErrorPanel } from "../../shell/ErrorPanel";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { VotersApi, type VoterCount } from "./api";

/**
 * Map view: one circle per Zambia province, sized by registered voters,
 * coloured by "ours-percentage". Province centroids are hardcoded since
 * district records don't yet carry coordinates. Click a circle to see the
 * province's totals + share-of-national.
 */

// Approximate centroids of Zambia's 10 provinces (decimal degrees).
// Sourced from province-capital coordinates, adjusted to roughly the
// geographic centre of the province. Good enough for map-marker placement;
// not authoritative for any geospatial calculation.
const PROVINCE_CENTROIDS: Record<string, [number, number]> = {
  "Central":        [-14.00, 28.40],
  "Copperbelt":     [-12.83, 28.20],
  "Eastern":        [-13.65, 32.65],
  "Luapula":        [-10.40, 28.65],
  "Lusaka":         [-15.42, 28.30],
  "Muchinga":       [-11.50, 31.45],
  "Northern":       [-10.20, 31.20],
  "North-Western":  [-12.18, 26.42],
  "Southern":       [-16.50, 27.40],
  "Western":        [-15.25, 23.13],
};

const ZAMBIA_CENTER: [number, number] = [-13.13, 27.85];
const ZAMBIA_BOUNDS = new LatLngBounds([-18.5, 21.0], [-7.5, 34.5]);

interface ProvinceAgg {
  province: string;
  registered: number;
  ours: number;
  districts: number;
}

export function VoterMap({ period }: { period: string }) {
  const counts = useQuery({
    queryKey: ["voter-counts", "district", period],
    queryFn: () => VotersApi.list({ scopeLevel: "district", period }),
  });
  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });

  const byProvince = useMemo<ProvinceAgg[]>(() => {
    const districtProv = new Map<string, string>();
    for (const d of districts.data?.items ?? []) {
      // The seeded Zambia districts carry a `province` field that the API
      // passes through transparently. Cast to access without breaking the
      // narrower District type that the admin uses.
      const prov = (d as unknown as { province?: string }).province;
      if (prov) districtProv.set(d.id, prov);
    }
    const m = new Map<string, ProvinceAgg>();
    for (const c of counts.data?.items ?? []) {
      const prov = districtProv.get(c.scopeId);
      if (!prov) continue;
      const cur = m.get(prov) ?? { province: prov, registered: 0, ours: 0, districts: 0 };
      cur.registered += c.registered;
      cur.ours += c.ours;
      cur.districts += 1;
      m.set(prov, cur);
    }
    return Array.from(m.values());
  }, [counts.data, districts.data]);

  const totalRegistered = byProvince.reduce((s, p) => s + p.registered, 0);
  const maxRegistered = Math.max(1, ...byProvince.map((p) => p.registered));

  return (
    <div>
      {counts.error && <ErrorPanel err={counts.error} title="Couldn't load voter counts" />}
      {districts.error && <ErrorPanel err={districts.error} title="Couldn't load districts" />}

      <div style={{ height: 520, width: "100%", border: "1px solid var(--border)", borderRadius: 6 }}>
        <MapContainer
          center={ZAMBIA_CENTER}
          zoom={6}
          maxBounds={ZAMBIA_BOUNDS}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          {byProvince.map((p) => {
            const centroid = PROVINCE_CENTROIDS[p.province];
            if (!centroid) return null;
            // Radius scales with registered voters (sqrt for visual balance).
            const radius = 10 + Math.round(40 * Math.sqrt(p.registered / maxRegistered));
            const oursPct = p.registered > 0 ? p.ours / p.registered : 0;
            // Colour: red (low ours-pct) → green (high ours-pct).
            const color = oursPct >= 0.5 ? "#16a34a" : oursPct >= 0.25 ? "#f59e0b" : "#dc2626";
            return (
              <CircleMarker
                key={p.province}
                center={centroid}
                radius={radius}
                pathOptions={{ color, fillColor: color, fillOpacity: 0.45, weight: 2 }}
              >
                <Popup>
                  <div style={{ fontSize: 13, minWidth: 200 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.province} Province</div>
                    <div>Registered: <strong>{p.registered.toLocaleString()}</strong></div>
                    <div>Ours: <strong>{p.ours.toLocaleString()}</strong> ({(oursPct * 100).toFixed(1)}%)</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                      {p.districts} district{p.districts === 1 ? "" : "s"} ·{" "}
                      {totalRegistered > 0 ? ((p.registered / totalRegistered) * 100).toFixed(1) : "0.0"}% of national
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {byProvince.length === 0 && !counts.isLoading && !districts.isLoading && (
        <div className="muted" style={{ padding: 16, fontSize: 13 }}>
          No district-level voter counts found for period <code>{period}</code>. Run{" "}
          <code>python seed/seed_zambia_voter_stats.py</code> or capture some via{" "}
          <strong>+ New count</strong>.
        </div>
      )}

      <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
        Circle size scales with the registered-voter total. Colour reflects our share
        (red = under 25%, amber = 25–50%, green = 50%+). Province centroids are approximate
        and not authoritative for any geospatial measurement.
      </div>
    </div>
  );
}
